import { getLocationVDriveList, queryKeys } from '@vgrubs/components';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { LocationVDriveEntity } from 'components/src/types/location.types';

const PRELOAD_ROOT_MARGIN = '1000px';

const fetchVDriveList = async ({
  pageParam = 1,
  query,
}: {
  pageParam?: number;
  query: string;
}) => {
  const { data } = await getLocationVDriveList({
    limit: 20,
    page: pageParam,
    query: query || undefined,
  });

  const vDriveVerifications: LocationVDriveEntity[] = data?.data ?? [];
  const nextPage =
    vDriveVerifications.length === 20 ? pageParam + 1 : undefined;

  return {
    vDriveVerifications,
    nextPage,
  };
};

export const useVerificationsVDriveInfiniteScroll = (query: string) => {
  const observerRef = useRef<HTMLDivElement | null>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    refetch,
  } = useInfiniteQuery({
    queryKey: [...queryKeys.admin.verifications.vDrive, query],
    queryFn: ({ pageParam = 1 }) => fetchVDriveList({ pageParam, query }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const current = observerRef.current;
    if (!current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0,
        rootMargin: `0px 0px ${PRELOAD_ROOT_MARGIN} 0px`,
      }
    );

    observer.observe(current);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  const vDriveVerifications: LocationVDriveEntity[] =
    data?.pages.flatMap((page) => page.vDriveVerifications) ?? [];

  return {
    vDriveVerifications,
    observerRef,
    isFetchingNextPage,
    status,
    refetch,
  };
};
